import {useQueries, useQuery} from '@tanstack/react-query'
import {B2BMetricsRepository} from '../repositories/B2BMetricsRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {AgencyRepository} from '../repositories/AgencyRepository.ts'
import {B2BPackDetailResponse} from '../domain/entities/B2BPackDetail.ts'

const b2bMetricsRepository = new B2BMetricsRepository(new AuthenticationRepository())
const agencyRepository = new AgencyRepository(new AuthenticationRepository())

export function useB2BAgencyPacks(agencyId: string, isSelfService = false, enabled = true) {
    const packagesQuery = useQuery({
        queryKey: ['agency-packages', agencyId],
        queryFn: () => agencyRepository.getAgencyPackages(agencyId),
        enabled: !!agencyId && enabled,
    })

    // Dedupe defensively: the packages list has occasionally come back with the
    // same package listed twice, which used to just mean a repeated PackCard —
    // now that all packs pool into one flat list, a repeated id would show up
    // as visibly duplicated codes.
    const packIds = Array.from(new Set((packagesQuery.data ?? []).map((pkg) => pkg.id)))

    const detailQueries = useQueries({
        queries: packIds.map((packId) => ({
            queryKey: ['b2b-pack-detail', agencyId, packId],
            queryFn: () => b2bMetricsRepository.getB2BPackDetail(agencyId, packId, isSelfService),
            enabled: !!agencyId,
        })),
    })

    const packsById = new Map<string, B2BPackDetailResponse>()
    for (const query of detailQueries) {
        if (query.data) packsById.set(query.data.pack.id, query.data)
    }
    const packs = Array.from(packsById.values())
        .sort((a, b) => b.pack.purchasedAt.localeCompare(a.pack.purchasedAt))

    const isLoading = packagesQuery.isLoading || detailQueries.some((query) => query.isLoading)

    return {
        packs,
        packages: packagesQuery.data,
        isLoading,
        packsError: packagesQuery.error ?? detailQueries.find((query) => query.error)?.error,
    }
}
